<template>
	<div v-show="isVisible" ref="fieldsetRef" class="r3-fieldset">
		<!-- Quadro (borda lateral + inferior) -->
		<div class="fieldset-frame" />

		<!-- Título -->
		<span ref="titleRef" :style="{ fontSize: titleFontSize }" @click="onTitleClick">
			{{ fieldset.title }}
		</span>

		<!-- Conteúdo interno -->
		<div class="fieldset-content" :class="props.slotClass"
			:style="style ?? { fontSize: getFontSizeForWidth(screenWidth, 'slot') }">
			<slot />
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, onMounted, onBeforeUnmount } from 'vue';
	import { R3FieldSetClass } from '~/classes/base/R3FieldSet.classes';


	const fieldset = new R3FieldSetClass();

	const isVisible = computed(() => fieldset.visible);

	const emits = defineEmits<{
		'update:visible': [value: boolean];
		'titleClick': [event: MouseEvent];
	}>();

	const props = withDefaults(defineProps<{
		title: string;
		slotClass?: string;
		style?: Record<string, string>;
		visible?: boolean;
		labelSize?: string;
	}>(), {
		visible: true
	});

	// Título do fielset
	const titleFontSize = computed(() => {
		if(props.labelSize) {
			return props.labelSize;
		}

		return getFontSizeForWidth(screenWidth.value, 'title');
	})


	watch(() => props.title, newvalue => {

		fieldset.title = newvalue;

	}, { immediate: true });

	watch(() => props.visible, (newvalue) => {
		if (newvalue !== undefined) {
			fieldset.visible = newvalue;
		}
	}, { immediate: true });

	watch(() => fieldset.visible, (newvalue) => {
		emits('update:visible', newvalue);
	}, { immediate: true });



	const fieldsetRef = ref<HTMLElement | null>(null);
	const titleRef = ref<HTMLElement | null>(null);
	const screenWidth = ref(window.innerWidth);

	function getFontSizeForWidth(width: number, type: 'title' | 'slot') {
		if (width <= 240) return type === 'title' ? '8px' : '7px';
		// if (width <= 360) return type === 'title' ? '11px' : '10px';
		// if (width <= 480) return type === 'title' ? '12px' : '9px';

		if (type === 'title') {
			return 'clamp(12px, 1.5vw, 1.1rem)';
		}

		return '1rem';
	}

	function updateSpanMetrics() {

		if (fieldsetRef.value && titleRef.value) {
			const left = 5; // distância fixa da borda esquerda
			titleRef.value.style.left = `${left}px`; // aplica também no estilo do span
			const width = titleRef.value.offsetWidth;
			const afterLeft = left + width + 4;

			fieldsetRef.value.style.setProperty('--title-left', `${left}px`);
			fieldsetRef.value.style.setProperty('--title-width', `${width}px`);
			fieldsetRef.value.style.setProperty('--after-left', `${afterLeft}px`);
		}
	}

	let observer: ResizeObserver;

	onMounted(() => {

		screenWidth.value = window.innerWidth;

		updateSpanMetrics();

		if (props.title !== undefined) {
			fieldset.title = props.title;
		}

		// Observe mudança no tamanho do título
		if (titleRef.value) {
			observer = new ResizeObserver(updateSpanMetrics);
			observer.observe(titleRef.value);
		}

		// Recalcular ao redimensionar a janela
		window.addEventListener('resize', updateSpanMetrics);
	});

	onBeforeUnmount(() => {
		if (observer && titleRef.value) {
			observer.unobserve(titleRef.value);
		}
		window.removeEventListener('resize', updateSpanMetrics);
	});

	function onTitleClick(event: MouseEvent) {
		emits('titleClick', event);
	}

	type ExposeType = {
		// setFocus: typeof setFocus;
		//setInputValue: typeof setInputValue
		//inputRef: typeof inputRef;
	} & typeof fieldset;

	const expose = {
		// setFocus,
		//inputRef,
		//setInputValue
	} as ExposeType

	//defineExpose(expose);
	useExposeProperties(expose, fieldset)

	defineExpose(expose);

</script>

<style scoped>
	.r3-fieldset {
		position: relative;
		padding: 1em 0.5em 0.6em 0.5em;
		width: 100%;
		color: #333;
	}

	/* Quadro lateral + inferior */
	.fieldset-frame {
		position: absolute;
		top: 0.75em;
		left: 0;
		right: 0;
		bottom: 0;
		border: 1px solid #ccc;
		border-top: none;
		z-index: 0;
		pointer-events: none;
	}

	.r3-fieldset::before {
		content: '';
		position: absolute;
		top: 0.75em;
		left: 0;
		width: calc(var(--title-left, 5px) - 1px);
		/* termina 1px antes do título */
		height: 1px;
		background-color: #ccc;
		z-index: 1;
	}

	/* Linha direita da parte superior */
	.r3-fieldset::after {
		content: '';
		position: absolute;
		top: 0.75em;
		left: var(--after-left, 160px);
		right: 0;
		height: 1px;
		background-color: #ccc;
		z-index: 1;
	}

	/* Título à esquerda */
	.r3-fieldset>span {
		position: absolute;
		top: 0.2em;
		left: var(--title-left, 5px);
		/* Agora sempre 5px da esquerda */
		font-weight: bold;
		font-size: 14px;
		padding: 0 4px;
		z-index: 2;

		/* impede quebra de linha */
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	/* Conteúdo */
	.fieldset-content {
		padding-top: 0.5rem;
		width: 100%;
	}
</style>
